// @boty/ai - Question generation for clarification flow

import { analyzeContentGaps } from './content-analyzer';
import type {
  AIUsageCallback,
  ClarificationQuestion,
  ContentAnalysisResult,
} from './types';

/**
 * Options for generating clarification questions
 */
export interface GenerateQuestionsOptions {
  /** Bot ID for associating questions */
  botId: string;
  /** Number of questions to generate (default: 5) */
  maxQuestions?: number;
  /** Whether to filter out low priority questions (default: false) */
  filterLowPriority?: boolean;
  /** Optional callback for reporting LLM usage data */
  onUsage?: AIUsageCallback;
}

/**
 * Result from question generation
 */
export interface GenerateQuestionsResult {
  /** All generated questions (up to maxQuestions) */
  allQuestions: ClarificationQuestion[];
  /** Questions to display to user (high + medium priority only) */
  displayedQuestions: ClarificationQuestion[];
  /** Content analysis metadata */
  contentAnalysis: {
    totalCharacters: number;
    topicsIdentified: string[];
    gapsFound: number;
  };
}

/**
 * Generates clarification questions from bot content.
 *
 * @param content - Combined bot content to analyze
 * @param options - Generation options
 * @returns Questions with analysis metadata
 */
export async function generateClarificationQuestions(
  content: string,
  options: GenerateQuestionsOptions
): Promise<GenerateQuestionsResult> {
  const { botId, maxQuestions = 5, filterLowPriority = false, onUsage } = options;

  // Analyze content and generate questions
  const analysisResult: ContentAnalysisResult = await analyzeContentGaps(content, {
    botId,
    maxQuestions,
    ...(onUsage && { onUsage }),
  });

  // Filter questions based on priority
  const displayedQuestions = filterLowPriority
    ? analysisResult.questions.filter(
        (q) => q.priority === 'high' || q.priority === 'medium'
      )
    : analysisResult.questions;

  return {
    allQuestions: analysisResult.questions,
    displayedQuestions,
    contentAnalysis: {
      totalCharacters: content.length,
      topicsIdentified: analysisResult.topicsIdentified,
      gapsFound: analysisResult.gapsFound,
    },
  };
}

/**
 * Checks if all questions have been resolved (answered or skipped)
 *
 * @param questions - Array of clarification questions
 * @returns True if all questions are answered or skipped
 */
export function areAllQuestionsResolved(questions: ClarificationQuestion[]): boolean {
  return questions.every((q) => q.status === 'answered' || q.status === 'skipped');
}

/**
 * Gets summary statistics for a set of questions
 *
 * @param questions - Array of clarification questions
 * @returns Summary with counts by status
 */
export function getQuestionsSummary(questions: ClarificationQuestion[]): {
  total: number;
  answered: number;
  skipped: number;
  pending: number;
  totalCharactersAdded: number;
} {
  const answered = questions.filter((q) => q.status === 'answered');
  const skipped = questions.filter((q) => q.status === 'skipped');
  const pending = questions.filter((q) => q.status === 'pending');

  const totalCharactersAdded = answered.reduce(
    (sum, q) => sum + (q.answer?.length || 0),
    0
  );

  return {
    total: questions.length,
    answered: answered.length,
    skipped: skipped.length,
    pending: pending.length,
    totalCharactersAdded,
  };
}
